import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { Avatar, Badge, Box, IconButton, useMediaQuery } from "@mui/material";
import type React from "react";

import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import AppModulesPopover from "../../common/components/AppModulePopover/AppModulesPopover";
import NotificationPopover from "../../common/components/NotificationPopover/NotificationPopover";
import { useNotifications } from "../../common/hooks/useNotifications";
import { RootState } from "../../redux/store/store";
import { LocationPopover } from "./location-switching/LocationPopover";
import { ProfilePopover } from "./location-switching/ProfilePopover";
import { LocationOption } from "./location-switching/types";
import { useAwsControllerServiceGetFile } from "../../services/queries";
import MobileMenu from "./mobileMenu";
import ProviderMultiSelect from "./ProviderMultiSelect";
import { type NavbarProviderOption } from "../../redux/slices/ehr/navbarProvider.slice";
import { setPersistedNavbarProviders } from "../../redux/slices/auth.slice";
import { useNavbarProviderDefaults } from "../../common/hooks/useNavbarProviderDefaults";
import PatientSearch from "./PatientSearch";
import {
  NavbarOptions,
  getFilteredModuleOptions,
  getFilteredNavbarOptions,
} from "./widget/constants";
import ResponsiveNavTabs from "./ResponsiveNavTabs";

const Navbar = ({ selectedModule }: { selectedModule: string }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const menuOptions = NavbarOptions[selectedModule] || [];
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modulesAnchorEl, setModulesAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [locationAnchorEl, setLocationAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);
  const profile = useSelector((state: RootState) => state.auth.user);
  const avatarKey = profile?.avatar || "";
  const { data: avatarUrlData } = useAwsControllerServiceGetFile(
    { key: avatarKey },
    [],
    { enabled: !!avatarKey && !avatarKey.startsWith("http") },
  );
  const avatarSrc = avatarKey.startsWith("http")
    ? avatarKey
    : (avatarUrlData?.data as unknown as string) || undefined;
  const locationList: any = useSelector(
    (state: RootState) => state.auth.assignedLocations,
  );
  const { user } = useSelector((state: RootState) => state.auth);
  const { unreadCount } = useSelector((state: RootState) => state.notification);
  const userPermissions = useSelector(
    (state: RootState) => state.auth.permissions,
  );
  const isEmployerRole =
    user?.roleType === "EMPLOYER" ||
    user?.roleType === "AGENT" ||
    user?.roleType === "AFFILIATE";
  const isProviderRole =
    user?.roleType === "PROVIDER" || user?.roleType === "CLINIC_USER";
  const isBrokerRole = user?.roleType === "BROKER";
  const selectedProviders = useSelector(
    (state: RootState) => state.navbarProvider.selectedProviders,
  );
  // Only the user's own picks, so the dropdown reads empty on login even
  // though the filter is already scoped (own uuid / all location providers).
  const pickedProviders = useSelector(
    (state: RootState) => state.navbarProvider.pickedProviders,
  );

  // Seeds the base/fallback providers and keeps scheduling in step.
  useNavbarProviderDefaults(); //when this hook is called, we are just setting the right redux for the provider dropdown.

  // Saved in auth (persisted); the defaults hook mirrors it into the filter.
  const handleProvidersChange = (providers: NavbarProviderOption[]) => {
    dispatch(setPersistedNavbarProviders(providers));
  };

  const routerLocation = useLocation();
  const hasHeavyNavbarWidgets = selectedModule === "EHR" || isProviderRole;
  const mobileBreakpoint = hasHeavyNavbarWidgets
    ? "(max-width: 900px)"
    : "(max-width: 768px)";
  // const isSmallScreen = useMediaQuery("(max-width: 900px)");
  const isSmallScreen = useMediaQuery(mobileBreakpoint);
  const isVerySmallScreen = useMediaQuery("(max-width: 576px)");
  const isTabActive = useCallback(
    (optionPath: string) => {
      const currentPath = routerLocation.pathname;

      switch (optionPath) {
        case "orders":
          return (
            currentPath.startsWith("/ehr/orders") ||
            currentPath.startsWith("/ehr/rx") ||
            currentPath.startsWith("/ehr/cpoe")
          );
        case "all-employees":
          return (
            currentPath.startsWith("/employer/all-employees") ||
            currentPath.match(
              /\/employer-portal\/employers\/[^/]+\/(all-employees|employees)/,
            ) !== null ||
            (currentPath.includes("/all-employees/") &&
              currentPath.includes("/membership-details"))
          );
        case "employers":
          return currentPath.includes("/employer/employers/");
        case "payments":
          return (
            currentPath.match(
              /\/affiliate-portal\/affiliates\/[^/]+\/payments/,
            ) !== null ||
            currentPath.match(
              /\/affiliate-portal\/affiliates\/[^/]+\/invoice\/[^/]+/,
            ) !== null
          );
        case "memberships":
          return false;
        case "tickets":
          return (
            currentPath.startsWith("/employer/tickets") ||
            currentPath.startsWith("/employer/management/") ||
            currentPath.match(
              /\/employer-portal\/employers\/[^/]+\/(tickets|management)/,
            ) !== null
          );
        case "notifications":
        case "invoices":
        case "payment-option":
          return false;
        default:
          return false;
      }
    },
    [routerLocation.pathname],
  );

  const getFullPath = useCallback(
    (option: { path: string }) => {
      const basePath = NavbarOptions[selectedModule]?.basePath;

      let fullPath = option.path;

      if (basePath) {
        if (selectedModule === "BROKER_PORTAL" && user?.uuid) {
          fullPath = `/${basePath}/agent/${user.uuid}/${option.path}`;
        } else if (selectedModule === "EMPLOYER_PORTAL" && user?.employerUuid) {
          fullPath = `/${basePath}/${user.employerUuid}/${option.path}`;
        } else if (
          selectedModule === "AFFILIATE_PORTAL" &&
          user?.affiliateGroupUuid
        ) {
          fullPath = `/${basePath}/${user.affiliateGroupUuid}/${option.path}`;
        } else {
          fullPath = `/${basePath}/${option.path}`;
        }
      }

      return fullPath;
    },
    [selectedModule, user?.uuid, user?.employerUuid, user?.affiliateGroupUuid],
  );

  const isMediumScreen = useMediaQuery(
    "(min-width: 769px) and (max-width: 1100px)",
  );
  const isLargeScreen = useMediaQuery("(min-width: 1101px)");
  const shouldShowMenu = !isSmallScreen;
  const menuGap = isMediumScreen ? 4 : isLargeScreen ? 8 : 12;

  const filteredMenuOptions = getFilteredModuleOptions(
    menuOptions?.options || [],
    userPermissions,
    user,
  );

  const filteredNavbarOptions = getFilteredNavbarOptions(
    NavbarOptions,
    userPermissions,
    user,
  );
  const availableModules = Object.values(filteredNavbarOptions).filter(
    (item: any) => item.value !== "EMPLOYER_PORTAL",
  );
  const hasSingleModule = availableModules.length === 1;

  useNotifications();
  useEffect(() => {
    if (!isSmallScreen) setMobileOpen(false);
  }, [isSmallScreen]);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
    setLocationAnchorEl(null);
  };

  const handleLocationClick = (event: React.MouseEvent<HTMLElement>) => {
    const locationBox = event.currentTarget.closest(
      "[data-location-box]",
    ) as HTMLElement;
    setLocationAnchorEl(locationBox || event.currentTarget);
  };

  const handleLocationClose = () => {
    setLocationAnchorEl(null);
  };

  const handleLocationSelect = (location: LocationOption) => {
    setLocationAnchorEl(null);
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleModulesClick = (event: React.MouseEvent<HTMLElement>) => {
    setModulesAnchorEl(event.currentTarget);
  };

  const handleModulesClose = () => {
    setModulesAnchorEl(null);
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCartClick = () => {
    navigate("/inventory/cart");
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: isVerySmallScreen ? 1 : 3,
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          pr: 0,
          minWidth: 0,
        }}
      >
        {shouldShowMenu ? (
          <ResponsiveNavTabs
            options={filteredMenuOptions}
            getFullPath={getFullPath}
            isTabActive={isTabActive}
            isVerySmallScreen={isVerySmallScreen}
            isMediumScreen={isMediumScreen}
            menuGap={menuGap}
          />
        ) : (
          <Box />
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isSmallScreen ? 0 : 1.2,
            flexShrink: 0,
            ml: 1,
          }}
        >
          {isProviderRole && !isSmallScreen && (
            <ProviderMultiSelect
              value={pickedProviders}
              onChange={handleProvidersChange}
            />
          )}

          {selectedModule === "EHR" &&
            !routerLocation.pathname.includes("/ehr/patients/sign-up") && (
              <PatientSearch
                selectedProviderUuids={selectedProviders.map((p) => p.uuid)}
              />
            )}

          {selectedModule === "INVENTORY" && (
            <IconButton
              onClick={handleCartClick}
              sx={{
                width: 40,
                height: 40,
                "&:hover": {
                  backgroundColor: "#2F4853",
                },
              }}
              aria-label="Go to cart"
            >
              <ShoppingCartIcon sx={{ color: "#FFF" }} />
            </IconButton>
          )}

          {!isEmployerRole &&
            !hasSingleModule &&
            selectedModule !== "BROKER_PORTAL" && (
              <IconButton
                onClick={handleModulesClick}
                sx={{
                  width: 40,
                  height: 40,
                  "&:hover": {
                    backgroundColor: "#2F4853",
                  },
                }}
              >
                <AppsRoundedIcon sx={{ color: "#FFF" }} />
              </IconButton>
            )}

          <IconButton
            onClick={handleNotificationClick}
            sx={{
              width: 40,
              height: 40,
              "&:hover": {
                backgroundColor: "#2F4853",
              },
            }}
            aria-label="Notifications"
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon sx={{ color: "#FFF" }} />
            </Badge>
          </IconButton>

          {isSmallScreen && (
            <IconButton
              onClick={toggleMobileMenu}
              sx={{
                color: "#FFF",
                "&:hover": {
                  backgroundColor: "#2F4853",
                },
              }}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}

          <AppModulesPopover
            anchorEl={modulesAnchorEl}
            onClose={handleModulesClose}
            navbarOptions={NavbarOptions}
          />

          <NotificationPopover
            anchorEl={notificationAnchorEl}
            onClose={handleNotificationClose}
          />

          {!isSmallScreen && (
            <Box
              onClick={handleProfileClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "#2F4853",
                },
                transition: "background-color 0.2s ease",
              }}
            >
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: "#2F4853",
                  fontSize: 14,
                  fontWeight: "bold",
                }}
                alt={`${profile?.firstName}`}
                src={avatarSrc}
              >
                {profile?.firstName?.charAt(0) || "U"}
              </Avatar>
              <Box
                sx={{
                  color: "#FFF",
                  display: { xs: "none", lg: "none", xl: "block" },
                  maxWidth: "180px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {profile?.firstName || profile?.lastName
                  ? `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim()
                  : "Unknown User"}
              </Box>
              <ArrowDropDownIcon />
            </Box>
          )}

          <ProfilePopover
            anchorEl={profileAnchorEl}
            onClose={handleProfileClose}
            onLocationClick={handleLocationClick}
            locations={locationList?.content || []}
          />

          <LocationPopover
            anchorEl={locationAnchorEl}
            onClose={handleLocationClose}
            currentLocation={
              locationList?.content?.find((loc) => loc?.lastLoggedIn)
                ?.locationName ||
              profile?.location ||
              "NA"
            }
            onLocationSelect={handleLocationSelect}
          />
        </Box>
      </Box>

      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        selectedModule={selectedModule}
      />
    </>
  );
};

export default Navbar;
